import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CustomDatepickerI18n } from '@app/shared/utils/custom-datepicker-i18n';
import { NgbDateCustomParserFormatter } from '@app/shared/utils/dateformater';
import { Model } from './../../../models/clux/model-legacy';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDatepicker,
  NgbDatepickerI18n,
  NgbDatepickerNavigateEvent,
  NgbDateStruct,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';
import { first } from 'rxjs';
import { dateErrorTextObj, getNgbDate, isValidNgbDate } from './filters-date.helper';
import { NgClass, NgTemplateOutlet, LowerCasePipe } from '@angular/common';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
@Component({
  selector: 'app-filters-dates',
  templateUrl: './filters-dates.component.html',
  styleUrls: ['./filters-dates.component.scss'],
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, NgbDatepicker, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, NgbInputDatepicker, MatError, NgTemplateOutlet, MatButton, LowerCasePipe],
})
export class FiltersDatesComponent implements OnInit {
  public elm = inject(ElementRef);
  public rendrer = inject(Renderer2);
  public calendar = inject(NgbCalendar);
  private fb = inject(UntypedFormBuilder);
  private ngZone = inject(NgZone);

  @ViewChild('dp') public datePicker: NgbDatepicker;
  @Input() public filterTitle: string;
  @Input() public filterOptions: Model.DateOptions;
  @Input() public rightJustify: boolean;
  @Output() public applyFilter = new EventEmitter<Model.DateOptions>();
  @Output() public closePopUp = new EventEmitter();
  @Input() public maxEndDate: NgbDate;
  @Input() public datesToEnable: string[];
  @Input() public isDateSelected: boolean;
  @Output() public selectedMonths = new EventEmitter<NgbDatepickerNavigateEvent>();
  public hoveredDate: NgbDate;
  public fromDate: NgbDateStruct;
  public toDate: NgbDateStruct;
  public datePickerSettings: Model.DateSettings;

  public minimumDate: NgbDate;
  public maximumDate: NgbDate;
  public outsideDays: 'visible' | 'collapsed' | 'hidden';
  public isAllSelectedOptions: boolean;
  public dateRangeForm: UntypedFormGroup;
  public enterDateManually: boolean;
  public startDate: Model.NextDate;
  public errorTextObj: Record<string, string> = dateErrorTextObj;
  constructor() {
    const calendar = this.calendar;

    this.minimumDate = calendar.getToday();
    this.maximumDate = calendar.getToday();
    this.minimumDate.year = calendar.getToday().year - 3;
    this.enterDateManually = false;
    this.startDate = { year: dayjs().year(), month: dayjs().month() };
  }

  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement: HTMLElement): void {
    if (targetElement) {
      if (
        targetElement.tagName === 'HTML' ||
        (!targetElement.classList.contains('active-filter-link') &&
          !targetElement.classList.contains('btn-light') &&
          !targetElement.classList.contains('no-border-btn') &&
          !targetElement.offsetParent.classList.contains('filter-container') &&
          !targetElement.classList.contains('mat-mdc-button-wrapper') &&
          !targetElement.offsetParent.classList.contains('filter-category') &&
          !targetElement.offsetParent.classList.contains('mat-mdc-form-field-infix') &&
          !targetElement.classList.contains('mat-mdc-form-field-infix') &&
          !targetElement.classList.contains('mat-mdc-form-field-flex') &&
          !targetElement.offsetParent.classList.contains('mat-mdc-form-field-wrapper') &&
          !targetElement.offsetParent.classList.contains('mat-mdc-text-field-wrapper') &&
          !targetElement.offsetParent.classList.contains('dropdown-menu') &&
          !targetElement.classList.contains('ngb-dp-month-name') &&
          !targetElement.classList.contains('ngb-dp-arrow-btn') &&
          !targetElement.classList.contains('mat-mdc-button-touch-target') &&
          !targetElement.classList.contains('mdc-checkbox__native-control') &&
          !targetElement.classList.contains('mdc-button__label'))
      ) {
        this.closePopUp.emit();
      }
    }
  }
  public ngOnInit(): void {
    this.setDefaultValue();
    this.datePickerSettings = {
      navigation: 'arrows',
      showWeekNumbers: false,
    };
    this.outsideDays = 'collapsed';
    this.dateRangeForm = this.fb.group(
      {
        selectedDate: [this.fromDate],
        selectedEndDate: [this.toDate],
      },
      {
        validators: [this.dateRangeValidator],
        updateOn: 'change',
      }
    );
  }

  public clearFilter(): void {
    if (this.filterOptions?.fromDate) {
      this.filterOptions.fromDate = '';
    }
    if (this.filterOptions?.toDate) {
      this.filterOptions.toDate = '';
    }
    this.fromDate = null;
    this.toDate = null;
    this.applyFilter.emit(null);
  }

  public apply(): void {
    const startDt = this.dateRangeForm.controls['selectedDate'].value;
    const endDt = this.dateRangeForm.controls['selectedEndDate'].value;

    const dateFilterOptions: Model.DateOptions = {
      fromDate: this.getFormatDate(this.fromDate || startDt),
      toDate: this.getFormatDate(this.toDate || endDt),
    };
    this.applyFilter.emit(dateFilterOptions);
  }

  public onDateSelection(date: NgbDate, isDateRange: boolean = true): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      return;
    }

    if (this.fromDate && !this.toDate && isDateRange) {
      this.toDate = date;
      return;
    }

    this.toDate = null;
    this.fromDate = date;
  }

  public isHovered(date: NgbDate): boolean {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  public isInside(date: NgbDate): boolean {
    return date.after(this.fromDate) && date.before(this.toDate);
  }

  public isRange(date: NgbDate): boolean {
    return date.equals(this.fromDate) || date.equals(this.toDate) || this.isInside(date) || this.isHovered(date);
  }

  public onKeyPress(event: KeyboardEvent): boolean {
    return (event.key >= '0' && event.key <= '9') || event.key === '/';
  }

  public backToCalendarView(): void {
    this.enterDateManually = false;
    this.ngZone.onStable
      .asObservable()
      .pipe(first())
      .subscribe(() => {
        if (this.datePicker) {
          (document.getElementsByClassName('ngb-dp-arrow-btn')[0] as HTMLElement).focus();
        }
      });
  }

  public onMonthChange(event: NgbDatepickerNavigateEvent): void {
    this.selectedMonths.emit(event);
  }

  private getFormatDate(date: NgbDateStruct): string {
    return date ? `${date.month}/${date.day}/${date.year}` : null;
  }

  public canApplyFilter(): boolean {
    if (this.enterDateManually) {
      const startDt = this.dateRangeForm.controls['selectedDate'].value;
      return isValidNgbDate(startDt) && this.dateRangeForm.valid;
    }
    return this.fromDate !== null || this.toDate !== null;
  }

  public ngbDateErrors(controlName: string): string[] {
    const errors = this.dateRangeForm.get(controlName).errors['ngbDate'];
    return errors ? Object.keys(errors).map((err) => err) : [];
  }

  public formGroupErrors(): string[] {
    return Object.keys(this.dateRangeForm.errors).map((err) => err);
  }

  private setDefaultValue(): void {
    if (this.filterOptions?.fromDate) {
      this.fromDate = getNgbDate(this.filterOptions.fromDate);
    } else {
      this.fromDate = null;
    }
    if (this.filterOptions?.toDate) {
      this.toDate = getNgbDate(this.filterOptions.toDate);
    } else {
      this.toDate = null;
    }
  }

  private dateRangeValidator: ValidationErrors = (): {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  } | null => {
    if (!this.dateRangeForm) {
      return null;
    }
    const startDt = this.dateRangeForm.controls['selectedDate'].value;
    const endDt = this.dateRangeForm.controls['selectedEndDate'].value;

    if (!startDt || !endDt) {
      return null;
    }

    if (!isValidNgbDate(startDt) && !isValidNgbDate(endDt)) {
      return null;
    }

    const startDate: NgbDate = new NgbDate(startDt.year, startDt.month, startDt.day);
    const endDate: NgbDate = new NgbDate(endDt.year, endDt.month, endDt.day);

    if (startDate.after(endDate)) {
      return { invalidRange: true };
    }

    return null;
  };
}
